import React, { CSSProperties, FC } from 'react'
import classNamesBind from 'classnames/bind'

import type { ColorIconName } from '@dashboard/lib/types'
import ColorIcon from '@dashboard/components/ColorIcon'
import { ProfileModuleName } from '@dashboard/lib/profile'

interface CardHeaderAction {
  title?: string
  icon?: ColorIconName
  tooltip?: string
  onClick?: () => void
}

interface OwnProps {
  title: string
  cardId?: ProfileModuleName
  cardName?: string
  icon?: ColorIconName
  secondaryTitle?: string
  actions?: CardHeaderAction[]
  discType?: string
  colorHex?: string
  smallIcon?: boolean
  noBorderBottom?: boolean
  flipIcon?: boolean
  children?: React.ReactNode
  grey?: boolean
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
  paywall?: boolean
}

import css from './styles.module.scss'
const classNames = classNamesBind.bind(css)

const CardHeader: FC<OwnProps> = ({
  title,
  cardId,
  cardName,
  noBorderBottom,
  onClick,
  children,
  icon,
  discType,
  colorHex,
  smallIcon,
  flipIcon,
  secondaryTitle,
  actions,
  grey,
  paywall,
}: OwnProps) => {
  // Functions
  const getDiscType = () => {
    if (discType) return discType
    else return ''
  }

  const handleClick =
    (onClick?: () => void) =>
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.stopPropagation()

      if (!onClick) return

      onClick()
    }

  const getTitleStyles = (): CSSProperties => {
    if (!colorHex) return {}

    return {
      color: colorHex,
    }
  }

  // Effects

  // Partials
  const renderIcon = () => {
    if (icon)
      return (
        <div className={classNames('icon', { flipIcon })}>
          <ColorIcon
            width={smallIcon ? 18 : 24}
            icon={icon}
            discType={discType || 'blue'}
          />
        </div>
      )
  }

  const rendersecondaryTitle = () => {
    if (!secondaryTitle) return null

    return <div className={classNames('secondaryTitle')}>{secondaryTitle}</div>
  }

  const renderChildren = () => {
    if (!children) return null
    return children
  }

  const renderActions = () => {
    if (!actions) return null

    return actions.reverse().map(action => {
      const { title, icon, tooltip, onClick } = action

      return (
        <div
          key={`${action.title}-${action.tooltip}`}
          className={classNames('subtitle-container', {
            hasAction: onClick,
            hasChildren: children,
            isDisabled: paywall,
          })}
          onClick={handleClick(onClick)}
        >
          {renderActionIcon(icon, tooltip)}
          <div
            className={classNames('subtitle-text', {
              hasTooltip: tooltip,
            })}
          >
            {title}
          </div>
          {renderActionTooltip(tooltip)}
        </div>
      )
    })
  }

  const renderActionIcon = (icon?: ColorIconName, tooltip?: string) => {
    if (!icon) return null

    return (
      <div className={classNames('subtitle-img', { hasTooltip: tooltip })}>
        <ColorIcon icon={icon} width={14} color={colorHex} />
      </div>
    )
  }

  const renderActionTooltip = (tooltip?: string) => {
    if (!tooltip) return null

    return <div className={classNames('subtitle-tooltip')}>{tooltip}</div>
  }

  return (
    <div
      className={classNames('container', getDiscType(), {
        noBorderBottom,
        onClick,
        grey,
      })}
      onClick={onClick}
    >
      <div className={classNames('header')}>
        {renderIcon()}
        <div
          style={getTitleStyles()}
          className={classNames('title', getDiscType())}
        >
          <h3 data-card-id={cardId} data-card-name={cardName}>
            {title}
          </h3>
          {rendersecondaryTitle()}
        </div>
      </div>
      {renderActions()}
      {renderChildren()}
    </div>
  )
}

export default CardHeader
